import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_boolean,
  MemberCoursesPlayedClient,
  MemberCoursesPlayedSummaryDTO,
  MemberStatsDTO,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';

// Takes the course id, resolves with the backend's ApiResponse<bool>.
type MemberCourseMutation = UseMutationResult<
  ApiResponse_boolean,
  unknown,
  string
>;

const apiClient = new MemberCoursesPlayedClient(API_BASE_URL, axiosInstance);

const MY_COURSES_PLAYED_KEY = ['myCoursesPlayed'];

// The logged-in member's own played courses (already resolved with course name/address/logo
// and pin name, see GET /member/me/courses-played on the backend), in the member's rank order:
// their favourite first, and any course not ranked yet after the ranked ones, newest first.
export const useMyCoursesPlayed = (): UseQueryResult<
  MemberCoursesPlayedSummaryDTO[]
> =>
  useQuery<MemberCoursesPlayedSummaryDTO[]>({
    queryKey: MY_COURSES_PLAYED_KEY,
    queryFn: async () => {
      const response = await apiClient.getMyCoursesPlayed();
      return response?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

// The member's dashboard numbers (GET /member/me/stats). staleTime 0, so the dashboard always
// re-reads them on arrival: they change whenever a course is ticked on Explore or added on a member
// page, and chasing every one of those writes with an invalidation is more fragile than one small
// request per visit.
export const useMyStats = (): UseQueryResult<MemberStatsDTO | null> =>
  useQuery<MemberStatsDTO | null>({
    queryKey: ['myStats'],
    queryFn: async () => {
      const response = await apiClient.getMyStats();
      return response?.data ?? null;
    },
    staleTime: 0,
  });

// The same member-scoped PUT/DELETE routes the Explore tick uses. Adding is where the free tier's
// limit bites: a capped member's 31st course comes back 403, which the caller turns into the
// upgrade offer (see freeLimitMessageFrom in useMyCourseFlags).
export const useAddMyCoursePlayed = (): MemberCourseMutation => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) =>
      apiClient.addCourseToMyCoursesPlayed(courseId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_COURSES_PLAYED_KEY });
    },
  });
};

export const useRemoveMyCoursePlayed = (): MemberCourseMutation => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) =>
      apiClient.removeCourseFromMyCoursesPlayed(courseId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_COURSES_PLAYED_KEY });
    },
  });
};

interface RankMutationContext {
  previous: MemberCoursesPlayedSummaryDTO[] | undefined;
}

export interface RankChange {
  // The ranked courses, favourite first.
  courseIds: string[];
  // The course that was moved, when this save is one move. The backend gives it the sentiment group
  // of its new neighbours, so the groups stay Great above Good above Meh.
  movedCourseId?: string;
}

// Saves the member's ranking: the whole list of course ids, favourite first. The cached list is
// reordered before the request goes, so a move shows at once, and put back if the save fails.
// Ids are compared lower-cased, per CLAUDE.md.
export const useSetMyCoursesPlayedRank = (): UseMutationResult<
  ApiResponse_boolean,
  unknown,
  RankChange,
  RankMutationContext
> => {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse_boolean,
    unknown,
    RankChange,
    RankMutationContext
  >({
    mutationFn: ({ courseIds, movedCourseId }: RankChange) =>
      apiClient.setMyCoursesPlayedRank({ courseIds, movedCourseId }),
    onMutate: async ({ courseIds }) => {
      await queryClient.cancelQueries({ queryKey: MY_COURSES_PLAYED_KEY });

      const previous = queryClient.getQueryData<
        MemberCoursesPlayedSummaryDTO[]
      >(MY_COURSES_PLAYED_KEY);

      if (previous != null) {
        const position = new Map(
          courseIds.map((id, index) => [id.toLowerCase(), index]),
        );
        const at = (row: MemberCoursesPlayedSummaryDTO): number =>
          position.get(row.courseId?.toLowerCase() ?? '') ??
          Number.MAX_SAFE_INTEGER;

        queryClient.setQueryData<MemberCoursesPlayedSummaryDTO[]>(
          MY_COURSES_PLAYED_KEY,
          [...previous].sort((a, b) => at(a) - at(b)),
        );
      }

      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous != null) {
        queryClient.setQueryData(MY_COURSES_PLAYED_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_COURSES_PLAYED_KEY });
    },
  });
};

export interface PlaceCourse {
  courseId: string;
  // CourseSentiment: 1 Meh, 2 Good, 3 Great.
  sentiment: number;
  // The course's new place, 1 being the favourite.
  position: number;
}

// The end of the ranking flow for one course (PUT /member/me/courses-played/{courseId}/place).
// Resolves with the course's new rank. Waits for the re-read of the list, so the result screen
// that follows already has the new order and scores to show.
export const usePlaceMyCoursePlayed = (): UseMutationResult<
  number | undefined,
  unknown,
  PlaceCourse
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ courseId, sentiment, position }: PlaceCourse) => {
      const response = await apiClient.placeMyCoursePlayed(courseId, {
        sentiment,
        position,
      });
      await queryClient.refetchQueries({ queryKey: MY_COURSES_PLAYED_KEY });
      return response?.data;
    },
  });
};
